import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAxios } from '../../contexts/AuthContext';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { AdminHeader } from '../../components/AdminHeader';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAuditLog() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLogs(); }, [page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/admin/audit-log?page=${page}&limit=30`);
      setLogs(data.logs);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      console.error('Failed:', err);
    }
    setLoading(false);
  };

  const actionColor = (action) => {
    if (action.includes('create')) return 'bg-green-50 text-green-700';
    if (action.includes('update')) return 'bg-blue-50 text-blue-700';
    if (action.includes('delete') || action.includes('deactivate')) return 'bg-red-50 text-red-600';
    if (action.includes('trial')) return 'bg-purple-50 text-purple-700';
    if (action.includes('finance')) return 'bg-amber-50 text-amber-700';
    return 'bg-gray-50 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <AdminHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-sans font-bold text-[#1C1C1E]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Audit Log ({total} entries)
          </h1>
        </div>

        {loading ? <p className="text-[#8E8E93]">Loading...</p> : logs.length === 0 ? (
          <Card className="border-[#E5E5EA] bg-white rounded-2xl text-center p-8">
            <History className="w-10 h-10 text-[#8E8E93] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-[#8E8E93]">No audit entries yet.</p>
          </Card>
        ) : (
          <>
            <div className="space-y-2">
              {logs.map((log) => (
                <div key={log.id} className="bg-white border border-[#E5E5EA] rounded-2xl p-4 flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Clock className="w-4 h-4 text-[#8E8E93]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${actionColor(log.action)} border-0 text-xs`}>
                        {log.action?.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-sm font-medium text-[#1C1C1E]">{log.employee_name}</span>
                      <span className="text-xs text-[#8E8E93]">
                        on {log.entity_type} &middot; {new Date(log.timestamp).toLocaleString('en-IN')}
                      </span>
                    </div>
                    {log.changes && Object.keys(log.changes).length > 0 && (
                      <div className="mt-2 text-xs text-[#8E8E93] bg-[#F5F5F5] p-2 rounded-2xl">
                        {Object.entries(log.changes).map(([k, v]) => (
                          <span key={k} className="inline-block mr-3">
                            <strong>{k}:</strong> {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-[#8E8E93]">Page {page} of {pages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="border-[#E5E5EA] rounded-2xl" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="border-[#E5E5EA] rounded-2xl" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
